import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withLocalize } from 'react-localize-redux';
import { useFormikContext } from 'formik-2';
import { Spinner } from '../../../components/widgets';
import css from './WizardContainer.css';
import { getNextStepIndex, getPrevStepIndex } from '../../../utils/wizard';
import WizardEditor from './WizardEditor';
import { TEST_WIZARD_STEPS, TEST_WIZARD_STEPS_NESTED } from './consts';
import { useFetchDistributionData } from '../../hooks';
import { getRequiredFieldsForStep } from './sectionsConfig';

const WizardContainer = ({ onClose, submitData, translate, activeStepIndex, changeWizardStep }) => {
    const { submitForm, setFieldValue, values, validateForm, setTouched } = useFormikContext();
    const { isLoading, data: distributionData } = useFetchDistributionData(translate);

    const validateFormForStep = useCallback(async () => {
        const currentStepKey = TEST_WIZARD_STEPS[activeStepIndex];
        const fieldsToValidate = getRequiredFieldsForStep(currentStepKey);
        const validationErrors = await validateForm();
        setTouched(validationErrors, true);
        return fieldsToValidate.some(field => validationErrors[field]);
    }, [activeStepIndex, setTouched, validateForm]);

    const onNextClick = useCallback(async () => {
        const isFormInvalid = await validateFormForStep();
        if (isFormInvalid) return;

        changeWizardStep(() => {
            return getNextStepIndex(activeStepIndex, TEST_WIZARD_STEPS);
        });
    }, [changeWizardStep, validateFormForStep, activeStepIndex]);

    const onPrevClick = useCallback(() => {
        changeWizardStep(currentStep => getPrevStepIndex(currentStep));
    }, [changeWizardStep]);

    useEffect(() => {
        if (activeStepIndex >= TEST_WIZARD_STEPS.length) {
            submitForm().then();
        }
    }, [activeStepIndex, submitForm, values]);

    const onStepClick = useCallback(
        async stepKey => {
            const targetStepIndex = TEST_WIZARD_STEPS.includes(stepKey)
                ? TEST_WIZARD_STEPS.indexOf(stepKey)
                : TEST_WIZARD_STEPS_NESTED[stepKey];
            if (await validateFormForStep()) return;

            changeWizardStep(targetStepIndex);
        },
        [changeWizardStep, validateFormForStep]
    );

    const onDraftClick = useCallback(
        async activeKey => {
            await setFieldValue('isDraft', true).then();
            await setFieldValue('draftStep', activeKey).then();
            await submitForm();
        },
        [setFieldValue, submitForm]
    );

    return (
        <>
            {isLoading ? (
                <Spinner show expanded />
            ) : (
                <div className={css.content}>
                    <div className={css.contentContainer}>
                        <WizardEditor
                            submitData={submitData}
                            distributionsData={distributionData}
                            activeStepIndex={activeStepIndex}
                            onPrevClick={onPrevClick}
                            onStepClick={onStepClick}
                            onClose={onClose}
                            onNextClick={onNextClick}
                            onDraftClick={onDraftClick}
                        />
                    </div>
                </div>
            )}
        </>
    );
};

WizardContainer.propTypes = {
    onClose: PropTypes.func.isRequired,
    translate: PropTypes.func,
    changeWizardStep: PropTypes.func.isRequired,
    activeStepIndex: PropTypes.number,
    submitData: PropTypes.shape({
        loading: PropTypes.bool,
        error: PropTypes.bool,
    }),
};

export default withLocalize(WizardContainer);
